/**
 * Change Detector - New Implementation
 *
 * Detects and categorizes changes between two inspection builder configurations.
 * Simplified without language dictionary - uses direct field values.
 */
import _ from "lodash";

/**
 * Map internal field type values to user-friendly display names
 */
const getFieldTypeDisplayName = (type) => {
	const typeMap = {
		text: "Text",
		int: "Integer",
		float: "Decimal",
		bool: "Checkbox",
		select: "Dropdown",
		photo: "Photo",
	};
	return typeMap[type] || type;
};

/**
 * Strip ALL leading [ and trailing ] brackets from a string
 * Handles legacy format with accumulated brackets like "[[utility]]" -> "utility"
 */
const stripBrackets = (str) => {
	if (typeof str !== "string") return str;
	return str.replace(/^\[+|\]+$/g, "");
};

/**
 * Normalize assetType to always be an array of clean strings
 * Handles cases where API returns string, array, or undefined
 * Also strips brackets from values (handles legacy format inconsistencies)
 */
const normalizeAssetType = (assetType) => {
	if (!assetType) return [];
	if (Array.isArray(assetType)) {
		return assetType.map(stripBrackets);
	}
	if (typeof assetType === "string") {
		return [stripBrackets(assetType)];
	}
	return [];
};

/**
 * Build a lookup map from typeId to detailType (human-readable name)
 */
const buildInspectionTypeLookup = (inspectionDetails) => {
	const lookup = new Map();
	(inspectionDetails || []).forEach((insp) => {
		if (insp.typeId) {
			lookup.set(insp.typeId, insp.detailType || insp.typeId);
		}
	});
	return lookup;
};

/**
 * Detects and categorizes changes between two inspection builder configurations
 * @param {Object} currentConfig - The current/modified configuration
 * @param {Object} originalConfig - The original/baseline configuration
 * @returns {Array} Array of change objects with type, category, description, and details
 */
export const detectConfigChanges = (currentConfig, originalConfig) => {
	if (!currentConfig || !originalConfig) {
		return [];
	}

	const changes = [];

	if (currentConfig === originalConfig) {
		return changes;
	}

	// Build lookup for inspection type names (combine both configs to handle added/removed types)
	const allInspectionDetails = [
		...(currentConfig.inspectionDetails || []),
		...(originalConfig.inspectionDetails || []),
	];
	const inspectionTypeLookup = buildInspectionTypeLookup(allInspectionDetails);

	// Compare zone layouts
	const zoneLayoutChanges = detectZoneLayoutChanges(
		currentConfig.zoneLayouts || [],
		originalConfig.zoneLayouts || [],
		inspectionTypeLookup
	);
	changes.push(...zoneLayoutChanges);

	// Compare inspection types (stored as inspectionDetails in config)
	const inspectionChanges = detectInspectionTypeChanges(
		currentConfig.inspectionDetails || [],
		originalConfig.inspectionDetails || []
	);
	changes.push(...inspectionChanges);

	return changes;
};

/**
 * Detect changes in zone layouts
 */
const detectZoneLayoutChanges = (currentLayouts, originalLayouts, inspectionTypeLookup) => {
	const changes = [];

	const currentMap = new Map(currentLayouts.map((layout) => [layout.layoutId, layout]));
	const originalMap = new Map(originalLayouts.map((layout) => [layout.layoutId, layout]));

	// Check for added layouts
	currentMap.forEach((layout, id) => {
		if (!originalMap.has(id)) {
			changes.push({
				type: "added",
				category: "Zone Layout",
				description: `Added zone layout: ${layout.layoutName}`,
				details: {
					layoutId: id,
					assetTypes: normalizeAssetType(layout.assetType).join(", ") || "Unknown",
					zoneCount: layout.configZones?.length || 0,
				},
			});
		}
	});

	// Check for removed layouts
	originalMap.forEach((layout, id) => {
		if (!currentMap.has(id)) {
			changes.push({
				type: "removed",
				category: "Zone Layout",
				description: `Removed zone layout: ${layout.layoutName}`,
				details: {
					layoutId: id,
					assetTypes: normalizeAssetType(layout.assetType).join(", ") || "Unknown",
				},
			});
		}
	});

	// Check for modified layouts
	currentMap.forEach((currentLayout, id) => {
		const originalLayout = originalMap.get(id);
		if (originalLayout) {
			const layoutChanges = compareZoneLayout(currentLayout, originalLayout, inspectionTypeLookup);
			changes.push(...layoutChanges);
		}
	});

	return changes;
};

/**
 * Compare individual zone layout for changes
 */
const compareZoneLayout = (currentLayout, originalLayout, inspectionTypeLookup) => {
	const changes = [];
	const layoutName = currentLayout.layoutName || "Unknown Layout";

	// Check for layout name change - DIRECT comparison
	if (currentLayout.layoutName !== originalLayout.layoutName) {
		changes.push({
			type: "modified",
			category: "Zone Layout",
			description: `Renamed zone layout`,
			details: {
				layoutId: currentLayout.layoutId,
				oldValue: originalLayout.layoutName,
				newValue: currentLayout.layoutName,
			},
		});
	}

	// Check for assetType array changes
	const currentAssetTypes = normalizeAssetType(currentLayout.assetType);
	const originalAssetTypes = normalizeAssetType(originalLayout.assetType);
	const currentSorted = [...currentAssetTypes].sort();
	const originalSorted = [...originalAssetTypes].sort();

	if (JSON.stringify(currentSorted) !== JSON.stringify(originalSorted)) {
		changes.push({
			type: "modified",
			category: "Zone Layout",
			description: `Modified asset types for ${layoutName}`,
			details: {
				layoutId: currentLayout.layoutId,
				oldValue: originalAssetTypes.join(", ") || "None",
				newValue: currentAssetTypes.join(", ") || "None",
			},
		});
	}

	// Compare zones within the layout
	// Use zone name (tag) as primary key for matching since tagNumber can be changed
	const currentZones = currentLayout.configZones || [];
	const originalZones = originalLayout.configZones || [];

	const currentZoneMap = new Map(currentZones.map((zone) => [zone.tag, zone]));
	const originalZoneMap = new Map(originalZones.map((zone) => [zone.tag, zone]));

	// Check for added zones
	currentZoneMap.forEach((zone, zoneName) => {
		if (!originalZoneMap.has(zoneName)) {
			changes.push({
				type: "added",
				category: "Zone",
				description: `Added zone: ${zone.tag}`,
				details: {
					layoutName,
					layoutId: currentLayout.layoutId,
					zoneName: zone.tag,
					zoneType: zone.tagType,
					tagNumber: zone.tagNumber,
				},
			});
		}
	});

	// Check for removed zones
	originalZoneMap.forEach((zone, zoneName) => {
		if (!currentZoneMap.has(zoneName)) {
			changes.push({
				type: "removed",
				category: "Zone",
				description: `Removed zone: ${zone.tag}`,
				details: {
					layoutName,
					layoutId: originalLayout.layoutId,
					zoneName: zone.tag,
					tagNumber: zone.tagNumber,
				},
			});
		}
	});

	// Check for modified zones
	currentZoneMap.forEach((currentZone, zoneName) => {
		const originalZone = originalZoneMap.get(zoneName);
		if (originalZone) {
			// Check for zone number change
			if (currentZone.tagNumber !== originalZone.tagNumber) {
				changes.push({
					type: "modified",
					category: "Zone",
					description: `Changed zone number for "${currentZone.tag}"`,
					details: {
						layoutName,
						layoutId: currentLayout.layoutId,
						zoneName: currentZone.tag,
						oldValue: originalZone.tagNumber,
						newValue: currentZone.tagNumber,
					},
				});
			}

			// Check for zone type change
			// Normalize tagType values to match how zoneEditDialog normalizes them
			// Empty/null and legacy "zone" all default to "Zone" in the edit dialog
			const normalizeTagType = (tagType) => {
				if (!tagType) return "Zone";
				// Normalize legacy lowercase "zone" to "Zone"
				if (tagType === "zone") return "Zone";
				return tagType;
			};
			const normalizedCurrentTagType = normalizeTagType(currentZone.tagType);
			const normalizedOriginalTagType = normalizeTagType(originalZone.tagType);
			if (normalizedCurrentTagType !== normalizedOriginalTagType) {
				changes.push({
					type: "modified",
					category: "Zone",
					description: `Changed tag type for ${currentZone.tag}`,
					details: {
						layoutName,
						layoutId: currentLayout.layoutId,
						zoneName: currentZone.tag,
						oldValue: normalizedOriginalTagType,
						newValue: normalizedCurrentTagType,
					},
				});
			}

			// Check for component changes
			const currentComponents = currentZone.tagComponents || [];
			const originalComponents = originalZone.tagComponents || [];
			const sortedCurrent = [...currentComponents].sort();
			const sortedOriginal = [...originalComponents].sort();

			if (!_.isEqual(sortedCurrent, sortedOriginal)) {
				const added = sortedCurrent.filter((c) => !sortedOriginal.includes(c));
				const removed = sortedOriginal.filter((c) => !sortedCurrent.includes(c));

				let description = `Modified components for zone "${currentZone.tag}"`;
				if (added.length > 0 && removed.length === 0) {
					description = `Added ${added.length} component${added.length > 1 ? "s" : ""} to zone "${currentZone.tag}"`;
				} else if (removed.length > 0 && added.length === 0) {
					description = `Removed ${removed.length} component${removed.length > 1 ? "s" : ""} from zone "${currentZone.tag}"`;
				}

				changes.push({
					type: "modified",
					category: "Zone",
					description,
					details: {
						layoutName,
						layoutId: currentLayout.layoutId,
						zoneName: currentZone.tag,
						oldCount: originalComponents.length,
						newCount: currentComponents.length,
						added: added.length,
						removed: removed.length,
					},
				});
			}

			// Check for inspection type changes
			const currentInspectionTypes = currentZone.inspectionTypes || [];
			const originalInspectionTypes = originalZone.inspectionTypes || [];
			const sortedCurrentTypes = [...currentInspectionTypes].sort();
			const sortedOriginalTypes = [...originalInspectionTypes].sort();

			if (!_.isEqual(sortedCurrentTypes, sortedOriginalTypes)) {
				const added = sortedCurrentTypes.filter((t) => !sortedOriginalTypes.includes(t));
				const removed = sortedOriginalTypes.filter((t) => !sortedCurrentTypes.includes(t));

				// Look up human-readable names for inspection types
				const getInspectionTypeName = (typeId) => inspectionTypeLookup?.get(typeId) || typeId;
				const addedNames = added.map(getInspectionTypeName);
				const removedNames = removed.map(getInspectionTypeName);

				const zoneName = currentZone.tag;
				let description = `Modified inspection types for zone "${zoneName}"`;
				if (added.length > 0 && removed.length === 0) {
					description = `Added inspection types to zone "${zoneName}": ${addedNames.join(", ")}`;
				} else if (removed.length > 0 && added.length === 0) {
					description = `Removed inspection types from zone "${zoneName}": ${removedNames.join(", ")}`;
				}

				changes.push({
					type: "modified",
					category: "Zone",
					description,
					details: {
						layoutName,
						layoutId: currentLayout.layoutId,
						zoneName,
						addedNames,
						removedNames,
						oldCount: originalInspectionTypes.length,
						newCount: currentInspectionTypes.length,
					},
				});
			}

			// Check for position changes
			const currentPos = currentZone.assetViewLocation;
			const originalPos = originalZone.assetViewLocation;
			const positionChanged = !_.isEqual(currentPos, originalPos);

			if (positionChanged && currentPos && originalPos) {
				const gridWidth = currentLayout?.assetViewGrid?.[0] || 7;
				const gridHeight = currentLayout?.assetViewGrid?.[1] || 7;

				let oldRow, oldCol, newRow, newCol;

				if (Array.isArray(originalPos) && Array.isArray(currentPos)) {
					oldCol = originalPos[0] + 1;
					oldRow = originalPos[1] + 1;
					newCol = currentPos[0] + 1;
					newRow = currentPos[1] + 1;
				} else {
					return;
				}

				const visualization = generateGridVisualization(
					gridWidth,
					gridHeight,
					oldRow,
					oldCol,
					newRow,
					newCol
				);

				changes.push({
					type: "modified",
					category: "Zone",
					description: `Repositioned zone "${currentZone.tag}"`,
					details: {
						layoutName,
						layoutId: currentLayout.layoutId,
						zoneName: currentZone.tag,
						oldPosition: originalPos,
						newPosition: currentPos,
						gridMove: {
							from: { row: oldRow, col: oldCol },
							to: { row: newRow, col: newCol },
							visualization,
						},
					},
				});
			}
		}
	});

	// Compare asset view image
	if (currentLayout.assetViewId !== originalLayout.assetViewId) {
		changes.push({
			type: "modified",
			category: "Zone Layout",
			description: `Changed asset image for "${layoutName}"`,
			details: {
				layoutName,
				layoutId: currentLayout.layoutId,
				oldValue: originalLayout.assetViewId || "none",
				newValue: currentLayout.assetViewId || "none",
			},
		});
	}

	// Compare components (by index since components don't have unique IDs)
	const currentComponents = currentLayout.components || [];
	const originalComponents = originalLayout.components || [];

	// Check for added components
	if (currentComponents.length > originalComponents.length) {
		const addedCount = currentComponents.length - originalComponents.length;
		changes.push({
			type: "added",
			category: "Component",
			description: `Added ${addedCount} component${addedCount > 1 ? "s" : ""} to "${layoutName}"`,
			details: {
				layoutName,
				layoutId: currentLayout.layoutId,
				addedCount,
				oldCount: originalComponents.length,
				newCount: currentComponents.length,
			},
		});
	}

	// Check for removed components
	if (currentComponents.length < originalComponents.length) {
		const removedCount = originalComponents.length - currentComponents.length;
		changes.push({
			type: "removed",
			category: "Component",
			description: `Removed ${removedCount} component${removedCount > 1 ? "s" : ""} from "${layoutName}"`,
			details: {
				layoutName,
				layoutId: currentLayout.layoutId,
				removedCount,
				oldCount: originalComponents.length,
				newCount: currentComponents.length,
			},
		});
	}

	// Compare individual component properties (for components that exist in both)
	const minLength = Math.min(currentComponents.length, originalComponents.length);
	for (let i = 0; i < minLength; i++) {
		const currentComp = currentComponents[i];
		const originalComp = originalComponents[i];
		const componentName = currentComp.component || originalComp.component || `Component ${i + 1}`;

		// Check component name change
		if (currentComp.component !== originalComp.component) {
			changes.push({
				type: "modified",
				category: "Component",
				description: `Renamed component in "${layoutName}"`,
				details: {
					layoutName,
					layoutId: currentLayout.layoutId,
					property: "Name",
					oldValue: originalComp.component || "(empty)",
					newValue: currentComp.component || "(empty)",
				},
			});
		}

		// Check minSeverity change
		if (currentComp.minSeverity !== originalComp.minSeverity) {
			changes.push({
				type: "modified",
				category: "Component",
				description: `Changed min severity for "${componentName}" in "${layoutName}"`,
				details: {
					layoutName,
					layoutId: currentLayout.layoutId,
					componentName,
					property: "Min Severity",
					oldValue: originalComp.minSeverity,
					newValue: currentComp.minSeverity,
				},
			});
		}

		// Check maxSeverity change
		if (currentComp.maxSeverity !== originalComp.maxSeverity) {
			changes.push({
				type: "modified",
				category: "Component",
				description: `Changed max severity for "${componentName}" in "${layoutName}"`,
				details: {
					layoutName,
					layoutId: currentLayout.layoutId,
					componentName,
					property: "Max Severity",
					oldValue: originalComp.maxSeverity,
					newValue: currentComp.maxSeverity,
				},
			});
		}

		// Check conditions change
		const currentConditions = currentComp.conditions || [];
		const originalConditions = originalComp.conditions || [];
		const sortedCurrent = [...currentConditions].sort();
		const sortedOriginal = [...originalConditions].sort();

		if (!_.isEqual(sortedCurrent, sortedOriginal)) {
			const added = sortedCurrent.filter((c) => !sortedOriginal.includes(c));
			const removed = sortedOriginal.filter((c) => !sortedCurrent.includes(c));

			let description = `Modified conditions for "${componentName}" in "${layoutName}"`;
			if (added.length > 0 && removed.length === 0) {
				description = `Added ${added.length} condition${added.length > 1 ? "s" : ""} to "${componentName}" in "${layoutName}"`;
			} else if (removed.length > 0 && added.length === 0) {
				description = `Removed ${removed.length} condition${removed.length > 1 ? "s" : ""} from "${componentName}" in "${layoutName}"`;
			}

			changes.push({
				type: "modified",
				category: "Component",
				description,
				details: {
					layoutName,
					layoutId: currentLayout.layoutId,
					componentName,
					property: "Conditions",
					added,
					removed,
					oldCount: originalConditions.length,
					newCount: currentConditions.length,
				},
			});
		}
	}

	return changes;
};

/**
 * Detect changes in inspection types
 */
const detectInspectionTypeChanges = (currentInspections, originalInspections) => {
	const changes = [];

	const currentMap = new Map(
		currentInspections.map((insp) => [insp.typeId || insp.detailType, insp])
	);
	const originalMap = new Map(
		originalInspections.map((insp) => [insp.typeId || insp.detailType, insp])
	);

	// Check for added inspections
	currentMap.forEach((inspection, id) => {
		if (!originalMap.has(id)) {
			changes.push({
				type: "added",
				category: "Inspection Type",
				description: `Added inspection type: ${inspection.detailType}`,
				details: {
					inspectionId: id,
					inspectionType: inspection.inspectionType,
					fieldCount: inspection.formData?.length || 0,
				},
			});
		}
	});

	// Check for removed inspections
	originalMap.forEach((inspection, id) => {
		if (!currentMap.has(id)) {
			changes.push({
				type: "removed",
				category: "Inspection Type",
				description: `Removed inspection type: ${inspection.detailType}`,
				details: {
					inspectionId: id,
					inspectionType: inspection.inspectionType,
				},
			});
		}
	});

	// Check for modified inspections
	currentMap.forEach((currentInspection, id) => {
		const originalInspection = originalMap.get(id);
		if (originalInspection) {
			const inspectionChanges = compareInspectionType(
				currentInspection,
				originalInspection
			);
			changes.push(...inspectionChanges);
		}
	});

	return changes;
};

/**
 * Compare individual inspection type for changes
 */
const compareInspectionType = (currentInspection, originalInspection) => {
	const changes = [];
	const inspectionName = currentInspection.detailType || "Unknown Inspection";

	// Check for name change - DIRECT comparison
	if (currentInspection.detailType !== originalInspection.detailType) {
		changes.push({
			type: "modified",
			category: "Inspection Type",
			description: `Renamed inspection type`,
			details: {
				oldValue: originalInspection.detailType,
				newValue: currentInspection.detailType,
				inspectionId: currentInspection.typeId,
			},
		});
	}

	// Check for description change
	if (currentInspection.description !== originalInspection.description) {
		changes.push({
			type: "modified",
			category: "Inspection Type",
			description: `Updated description for "${inspectionName}"`,
			details: {
				inspectionName,
				oldValue: originalInspection.description,
				newValue: currentInspection.description,
			},
		});
	}

	// Check for flag changes
	const flags = [
		{ key: "enforceOrder", label: "Enforce Order" },
		{ key: "prepopulateDefects", label: "Prepopulate Defects" },
		{ key: "requiresReview", label: "Requires Review" },
		{ key: "zoneless", label: "Zoneless" },
		{ key: "associateDriver", label: "Associate Driver" },
		{ key: "legallyMandated", label: "Legally Mandated" },
	];

	flags.forEach((flag) => {
		if (currentInspection[flag.key] !== originalInspection[flag.key]) {
			const newValue = currentInspection[flag.key] ? "enabled" : "disabled";
			changes.push({
				type: "modified",
				category: "Inspection Property",
				description: `${flag.label} ${newValue} for "${inspectionName}"`,
				details: {
					inspectionName,
					property: flag.label,
					oldValue: originalInspection[flag.key] ? "enabled" : "disabled",
					newValue,
				},
			});
		}
	});

	// Compare form fields
	const currentFields = currentInspection.formData || [];
	const originalFields = originalInspection.formData || [];

	// Helper to get a display name for a field (for change descriptions)
	const getFieldDisplayName = (field) => {
		if (field.fieldName && field.fieldName.trim()) return field.fieldName;
		if (field.hint && field.hint.trim()) return `"${field.hint}"`;
		return "Unnamed Field";
	};

	// Match fields by hint (stable identifier) when field names might be empty or changed
	// This allows us to detect field name changes correctly
	const findMatchingOriginalField = (currentField, currentIndex) => {
		// First try to match by hint (most stable identifier)
		if (currentField.hint && currentField.hint.trim()) {
			const hintMatch = originalFields.findIndex(
				(f) => f.hint === currentField.hint
			);
			if (hintMatch !== -1) {
				return hintMatch;
			}
		}

		// If same number of fields and no hint match, match by position
		if (currentFields.length === originalFields.length) {
			return currentIndex;
		}

		// Try to match by fieldName if it exists
		if (currentField.fieldName && currentField.fieldName.trim()) {
			const nameMatch = originalFields.findIndex(
				(f) => f.fieldName === currentField.fieldName
			);
			if (nameMatch !== -1) {
				return nameMatch;
			}
		}

		return -1; // No match found - truly a new field
	};

	// Track which original fields have been matched
	const matchedOriginalIndices = new Set();

	// Check for modifications in current fields
	currentFields.forEach((currentField, currentIndex) => {
		const originalIndex = findMatchingOriginalField(currentField, currentIndex);

		// If the original index was already matched to another field, treat this as a new field
		const effectiveOriginalIndex = (originalIndex !== -1 && matchedOriginalIndices.has(originalIndex))
			? -1
			: originalIndex;

		if (effectiveOriginalIndex !== -1) {
			matchedOriginalIndices.add(effectiveOriginalIndex);
			const originalField = originalFields[effectiveOriginalIndex];
			const fieldDisplayName = getFieldDisplayName(currentField);
			const originalDisplayName = getFieldDisplayName(originalField);

			// Check for field name change
			if (currentField.fieldName !== originalField.fieldName) {
				changes.push({
					type: "modified",
					category: "Form Field",
					description: `Changed "Name" in "${inspectionName}"`,
					details: {
						inspectionName,
						fieldName: fieldDisplayName,
						property: "Name",
						oldValue: originalField.fieldName || "(empty)",
						newValue: currentField.fieldName || "(empty)",
					},
				});
			}

			// Check for hint change
			if (currentField.hint !== originalField.hint) {
				changes.push({
					type: "modified",
					category: "Form Field",
					description: `Changed "Hint Text" in "${inspectionName}"`,
					details: {
						inspectionName,
						fieldName: fieldDisplayName,
						property: "Hint Text",
						oldValue: originalField.hint || "(empty)",
						newValue: currentField.hint || "(empty)",
					},
				});
			}

			// Check for required flag change
			if (currentField.required !== originalField.required) {
				changes.push({
					type: "modified",
					category: "Form Field",
					description: `Changed "Required" in "${inspectionName}"`,
					details: {
						inspectionName,
						fieldName: fieldDisplayName,
						property: "Required",
						oldValue: originalField.required ? "Yes" : "No",
						newValue: currentField.required ? "Yes" : "No",
					},
				});
			}

			// Check for field type change
			if (currentField.fieldType !== originalField.fieldType) {
				changes.push({
					type: "modified",
					category: "Form Field",
					description: `Changed "Field Type" in "${inspectionName}"`,
					details: {
						inspectionName,
						fieldName: fieldDisplayName,
						property: "Field Type",
						oldValue: getFieldTypeDisplayName(originalField.fieldType),
						newValue: getFieldTypeDisplayName(currentField.fieldType),
					},
				});
			}

			// Check for reordering (only if no other changes and counts are same)
			if (
				currentIndex !== effectiveOriginalIndex &&
				currentFields.length === originalFields.length &&
				currentField.fieldName === originalField.fieldName &&
				currentField.hint === originalField.hint &&
				currentField.required === originalField.required &&
				currentField.fieldType === originalField.fieldType
			) {
				changes.push({
					type: "reordered",
					category: "Form Field",
					description: `Reordered field in "${inspectionName}": ${fieldDisplayName}`,
					details: {
						inspectionName,
						fieldName: fieldDisplayName,
						oldPosition: effectiveOriginalIndex + 1,
						newPosition: currentIndex + 1,
					},
				});
			}
		} else {
			// effectiveOriginalIndex === -1: Truly a new field (either no match, or match was already used)
			const displayName = getFieldDisplayName(currentField);
			changes.push({
				type: "added",
				category: "Form Field",
				description: `Added field to "${inspectionName}": ${displayName}`,
				details: {
					inspectionName,
					fieldName: displayName,
					fieldType: getFieldTypeDisplayName(currentField.fieldType),
					position: currentIndex + 1,
				},
			});
		}
	});

	// Check for removed fields (original fields that weren't matched)
	originalFields.forEach((field, index) => {
		if (!matchedOriginalIndices.has(index)) {
			const displayName = getFieldDisplayName(field);
			changes.push({
				type: "removed",
				category: "Form Field",
				description: `Removed field from "${inspectionName}": ${displayName}`,
				details: {
					inspectionName,
					fieldName: displayName,
					fieldType: getFieldTypeDisplayName(field.fieldType),
				},
			});
		}
	});

	return changes;
};

/**
 * Get a summary count of changes by category
 */
export const getChangeSummary = (changes) => {
	const summary = {
		total: changes.length,
		byCategory: {},
		byType: {},
	};

	changes.forEach((change) => {
		if (!summary.byCategory[change.category]) {
			summary.byCategory[change.category] = 0;
		}
		summary.byCategory[change.category]++;

		if (!summary.byType[change.type]) {
			summary.byType[change.type] = 0;
		}
		summary.byType[change.type]++;
	});

	return summary;
};

/**
 * Generates an SVG-based grid visualization showing zone movement
 */
const generateGridVisualization = (
	gridWidth,
	gridHeight,
	oldRow,
	oldCol,
	newRow,
	newCol
) => {
	const cellSize = 24;
	const padding = 12;
	const gridWidth_px = gridWidth * cellSize;
	const gridHeight_px = gridHeight * cellSize;
	const totalWidth = gridWidth_px + padding * 2;
	const totalHeight = gridHeight_px + padding;
	const strokeWidth = 1;
	const highlightStrokeWidth = 2;

	let cells = "";
	for (let row = 0; row < gridHeight; row++) {
		for (let col = 0; col < gridWidth; col++) {
			const x = padding + col * cellSize;
			const y = padding + row * cellSize;

			const isOldPosition = row === oldRow - 1 && col === oldCol - 1;
			const isNewPosition = row === newRow - 1 && col === newCol - 1;

			let fill, stroke, strokeWidthValue;

			if (isOldPosition) {
				fill = "rgba(255, 152, 0, 0.3)";
				stroke = "rgba(255, 152, 0, 0.8)";
				strokeWidthValue = highlightStrokeWidth;
			} else if (isNewPosition) {
				fill = "rgba(76, 175, 80, 0.3)";
				stroke = "rgba(76, 175, 80, 0.8)";
				strokeWidthValue = highlightStrokeWidth;
			} else {
				fill = "rgba(0, 0, 0, 0.05)";
				stroke = "rgba(0, 0, 0, 0.4)";
				strokeWidthValue = strokeWidth;
			}

			cells += `<rect x="${x}" y="${y}" width="${cellSize}" height="${cellSize}" fill="${fill}" stroke="${stroke}" stroke-width="${strokeWidthValue}" />`;
		}
	}

	const oldX = padding + (oldCol - 1) * cellSize + cellSize / 2;
	const oldY = padding + (oldRow - 1) * cellSize + cellSize / 2;
	const newX = padding + (newCol - 1) * cellSize + cellSize / 2;
	const newY = padding + (newRow - 1) * cellSize + cellSize / 2;

	const markers = `
		<circle cx="${oldX}" cy="${oldY}" r="9" fill="rgba(255, 152, 0, 0.9)" stroke="white" stroke-width="2" />
		<text x="${oldX}" y="${oldY + 1}" font-size="11" font-weight="bold" fill="white" text-anchor="middle" dominant-baseline="middle">F</text>
		<circle cx="${newX}" cy="${newY}" r="9" fill="rgba(76, 175, 80, 0.95)" stroke="white" stroke-width="2" />
		<text x="${newX}" y="${newY + 1}" font-size="11" font-weight="bold" fill="white" text-anchor="middle" dominant-baseline="middle">T</text>
	`;

	const svg = `
		<svg width="${totalWidth}" height="${totalHeight}" viewBox="0 0 ${totalWidth} ${totalHeight}" xmlns="http://www.w3.org/2000/svg">
			<rect x="${padding}" y="${padding}" width="${gridWidth_px}" height="${gridHeight_px}" fill="none" stroke="rgba(0, 0, 0, 0.6)" stroke-width="2" />
			${cells}
			${markers}
		</svg>
	`;

	return svg.trim();
};
